import { faChevronLeft } from "@fortawesome/pro-solid-svg-icons/faChevronLeft";
import { faChevronRight } from "@fortawesome/pro-solid-svg-icons/faChevronRight";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { styled } from "@mui/material/styles";
import * as React from "react";
import { useEffect, useImperativeHandle, useRef, useState } from "react";

const WIDHT_ARROW = 59;

const OuterContainer = styled("div")(
    ({ theme }) => ({
        position: "relative",
        textAlign: "center",
        width: "100%",
        display: "grid",
        alignItems: "center",
        justifyContent: "flex-start"
    })
);

const ScrollContainer = styled(
    "div",
    { shouldForwardProp: (propName: string) => !["showScrollbar", "displayScroll"].includes(propName) }
)<{ showScrollbar: boolean, displayScroll: boolean; }>(
    ({ theme, showScrollbar, displayScroll }) => ({
        display: "flex",
        alignItems: "center",
        maxWidth: "100%",
        overflowX: "scroll",
        scrollBehavior: "smooth",
        //        padding: theme.spacing(1),
        "&::-webkit-scrollbar":
            showScrollbar
                ? {
                    display: displayScroll ? undefined : "none"
                }
                : {
                    background: "transparent", /* make scrollbar transparent */
                    "-webkit-appearance": "none",
                    width: 0,
                    height: 0,
                }
    })
);

const ButtonWrapper = styled(
    "div",
    { shouldForwardProp: (propName: string) => !["showScrollbar", "direction"].includes(propName) }
)<{ showScrollbar: boolean, direction: "left" | "right"; }>(
    ({ theme, direction, showScrollbar }) => ({
        display: "flex",
        flexDirection: "row",
        position: "absolute",
        zIndex: 200,
        left: direction == "left" ? 0 : undefined,
        right: direction == "right" ? 0 : undefined,
        top: showScrollbar ? "5px" : 0,
        "&:before": direction == "right"
            ? {
                content: '""',
                width: "32px",
                backgroundImage: "linear-gradient(to right, rgba(255,0,0,0), rgb(245, 247, 252,1))",
            } : undefined,
        "&:after": direction == "left"
            ? {
                content: '""',
                width: "32px",
                backgroundImage: "linear-gradient(to right, rgb(245, 247, 252,1), rgba(255,0,0,0))"
            } : undefined
    })
);

const StyledButton = styled(
    "button",
    { shouldForwardProp: (propName: string) => !["showScrollbar"].includes(propName) }
)<{ showScrollbar: boolean; }>(
    ({ theme, showScrollbar }) => (
        showScrollbar ?
            {
                border: 0,
                background: theme.extended.workArea.background,
                color: theme.palette.muted.dark,
                fontSize: "25px",
                backgroundColor: theme.palette.paper.main,
                paddingTop: "12px",
                paddingBottom: "12px",
                opacity: 0.95,
                height: "57px"
            }
            :
            {
                border: 0,
                background: theme.extended.workArea.background,
                color: theme.palette.muted.dark,
                fontSize: "20px",
            }
    )
);

export type MprcRibbonSlideReachedPositions = {
    scrollX: number;
    start: boolean;
    end: boolean;
};

interface IProps extends React.PropsWithChildren {
    selectedRef?: HTMLElement;
    slideWidth?: number;
    showScrollbar?: boolean;
    showNavigationButtons?: boolean;
    onPositionChanged?: (position: MprcRibbonSlideReachedPositions) => void;
}

export type MprcRibbonScrollHandle = {
    slide: (value: number) => void;
    move: (value: number) => void;
};


const MprcRibbonScrollComponent: React.ForwardRefRenderFunction<MprcRibbonScrollHandle, IProps> = (props, forwardRef) => {

    const {
        selectedRef,
        onPositionChanged,
        children,
        slideWidth = 150,
        showScrollbar = false,
        showNavigationButtons = true
    } = props;

    /////////////////////////////////////////////////////////////////////////////////////////////////////////
    /////////////////////////////////////////////////////////////////////////////////////////////////////////

    useImperativeHandle(forwardRef, () => ({
        slide: (value) => slide(value),
        move: (value) => move(value)
    }));

    /////////////////////////////////////////////////////////////////////////////////////////////////////////
    /////////////////////////////////////////////////////////////////////////////////////////////////////////

    let scrlRef = useRef<HTMLDivElement | null>(null);
    let containerRef = useRef<HTMLDivElement | null>(null);
    const [scrollX, setScrollX] = useState(0);
    const [scrollEnd, setScrolEnd] = useState(false);

    const ro = useRef<ResizeObserver>(new ResizeObserver(entries => {
        scrollCheck();
    }));

    //Slide click
    const slide = (shift: number) => {
        if (scrlRef.current) {

            scrlRef.current.scrollLeft += shift;
            setScrollX(scrollX + shift);

            const endReached = Math.floor(scrlRef.current.scrollWidth - scrlRef.current.scrollLeft - 1) <= scrlRef.current.offsetWidth;
            setScrolEnd(endReached);
        }
    };

    //Move Scroll
    const move = (shift: number) => {
        if (scrlRef.current) {

            scrlRef.current.scrollLeft = shift;
            setScrollX(shift);

            const endReached = Math.floor(scrlRef.current.scrollWidth - scrlRef.current.scrollLeft - 1) <= scrlRef.current.offsetWidth;
            setScrolEnd(endReached);
        }
    };

    const scrollCheck = () => {
        if (scrlRef.current) {
            setScrollX(scrlRef.current.scrollLeft);
            if (Math.floor(scrlRef.current.scrollWidth - scrlRef.current.scrollLeft - 1) <= scrlRef.current.offsetWidth) {
                setScrolEnd(true);
            } else {
                setScrolEnd(false);
            }
        }
    };

    useEffect(() => {
        if (containerRef.current) ro.current.observe(containerRef.current);
    }, [containerRef.current]);

    useEffect(() => {
        if (scrlRef.current) ro.current.observe(scrlRef.current);
    }, [scrlRef.current]);

    useEffect(() => {
        if (onPositionChanged) {
            onPositionChanged({ scrollX, start: scrollX == 0, end: scrollEnd });
        }
    }, [scrollX, scrollEnd]);

    useEffect(() => {
        return () => ro.current.disconnect();
    }, []);

    useEffect(() => {
        if (selectedRef && scrlRef?.current) {
            const selectedWidth = selectedRef.offsetLeft + selectedRef.offsetWidth;
            const rightLimit = scrlRef.current.scrollLeft + scrlRef.current.offsetWidth;
            const itemIsVisible = selectedRef.offsetLeft > scrlRef.current.scrollLeft && selectedWidth < rightLimit;

            if (itemIsVisible)
                return;

            if (selectedWidth > scrlRef.current.offsetWidth && selectedRef.offsetLeft > scrlRef?.current.scrollLeft) {
                const move = selectedWidth - scrlRef.current.offsetWidth + WIDHT_ARROW - scrlRef.current.scrollLeft;
                slide(move);
            }
            else if (scrlRef.current.scrollLeft > 0 && scrlRef.current.scrollLeft > selectedRef.offsetLeft - WIDHT_ARROW) {
                const move = selectedRef.offsetLeft - scrlRef.current.scrollLeft - WIDHT_ARROW;
                slide(move);
            }
        }
    }, [selectedRef]);

    return (
        <OuterContainer ref={containerRef}>

            {
                showNavigationButtons! &&
                <ButtonWrapper direction="left" style={{ visibility: scrollX == 0 ? "hidden" : "visible" }} showScrollbar={showScrollbar!}>
                    <StyledButton
                        className="prev"
                        showScrollbar={showScrollbar!}
                        onClick={() => slide(0 - slideWidth!)}
                    >
                        <FontAwesomeIcon icon={faChevronLeft} />
                    </StyledButton>
                </ButtonWrapper>
            }

            <ScrollContainer ref={scrlRef} onScroll={scrollCheck} showScrollbar={showScrollbar!} displayScroll={scrollX > 0 || !scrollEnd}>
                {children}
            </ScrollContainer>

            {
                showNavigationButtons! &&
                <ButtonWrapper direction="right" style={{ visibility: scrollEnd ? "hidden" : "visible" }} showScrollbar={showScrollbar!}>
                    <StyledButton
                        className="next"
                        showScrollbar={showScrollbar!}
                        onClick={() => slide(slideWidth!)}
                    >
                        <FontAwesomeIcon icon={faChevronRight} />
                    </StyledButton>
                </ButtonWrapper>
            }

        </OuterContainer>
    );
};

export const MprcRibbonScroll = React.forwardRef(MprcRibbonScrollComponent);
